import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardInfo } from "@eventr/capacitor-keyboard";
import { SafeArea, SafeAreaInsets } from "capacitor-plugin-safe-area";
import { createContext, useContext } from "react";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { DEVTOOLS_ENABLED } from "../../consts";
import { ScreenZoom } from "../../plugins/screenZoomPlugin";

export type TWindowSize = {
  insets: SafeAreaInsets | undefined;
  /// Guesture controls take up less room and we can show stuff behind them. Buttons, less so, footer/navbar needs context of this... (ios never has buttons, fyi.)
  navBarBottom: "guestureIos" | "guestureAndroid" | "buttonBlock" | "webView";
};

export type TKeyboardInfoState = {
  info?: KeyboardInfo;
  openState: "didHide" | "willHide" | "didShow" | "willShow";
};

interface WindowState {
  windowSizes: TWindowSize;
  keyboardState: TKeyboardInfoState;
  backgroundImageUrl: string;
  setWindowSizes: (windowSizes: TWindowSize) => void;
  setKeyboardState: (keyboardState: TKeyboardInfoState) => void;
  setBackgroundImageUrl: (backgroundImageUrl: string) => void;
}

// Replaces the `windowSizes` / `keyboardState` / `backgroundImageUrl` preact signals.
//
// `create` is imported from "zustand" directly and NOT from feat/appState/resetState.
// That wrapper registers a store for `resetAllStores()`, which appEvent.slice calls on
// logout. The signals this replaces were never reset on logout, and resetting the safe
// areas there would collapse every inset until the next `safeAreaChanged` event — which
// on iOS may never arrive. No `persist` either: these are all derived from the device.
export const useWindowStore = create<WindowState>()(
  subscribeWithSelector(
    devtools(
      (set) => ({
        windowSizes: {
          insets: { insets: { top: 0, left: 0, right: 0, bottom: 0 } },
          navBarBottom: "webView",
        },
        keyboardState: { info: undefined, openState: "didHide" },
        backgroundImageUrl: "",
        setWindowSizes: (windowSizes) => set({ windowSizes }, false, "setWindowSizes"),
        setKeyboardState: (keyboardState) => set({ keyboardState }, false, "setKeyboardState"),
        setBackgroundImageUrl: (backgroundImageUrl) => set({ backgroundImageUrl }, false, "setBackgroundImageUrl"),
      }),
      { enabled: DEVTOOLS_ENABLED, name: "window" },
    ),
  ),
);

/// ˅˅˅ LEAF SELECTORS
//
// Signals subscribed per-signal, so a component reading `windowSizes.value.insets.top`
// re-rendered on any windowSizes write. These select a *number*, so zustand's Object.is
// check means no re-render unless that specific number moved. Strictly fewer renders,
// same rendered output. Use one hook per leaf rather than useShallow over an object —
// useShallow would coarsen the granularity back to what signals had.

/**
 * The top inset measured in the native view rendering it, where `ViewTopInsetProvider` has
 * measured one. Undefined elsewhere, and until it has been measured.
 */
export const ViewTopInsetContext = createContext<number | undefined>(undefined);

/** The top inset: the view's own, where it is measured, otherwise the window's. */
export const useTopInset = () => {
  const viewInset = useContext(ViewTopInsetContext);
  const windowInset = useWindowStore((s) => s.windowSizes.insets?.insets.top ?? 0);
  return viewInset ?? windowInset;
};
export const useBottomInset = () => useWindowStore((s) => s.windowSizes.insets?.insets.bottom ?? 0);
export const useRightInset = () => useWindowStore((s) => s.windowSizes.insets?.insets.right ?? 0);
export const useLeftInset = () => useWindowStore((s) => s.windowSizes.insets?.insets.left ?? 0);
export const useNavBarBottom = () => useWindowStore((s) => s.windowSizes.navBarBottom);
export const useBackgroundImageUrl = () => useWindowStore((s) => s.backgroundImageUrl);

/** True while the keyboard is shown or animating in — the derivation done at four call sites. */
export const useKeyboardOpen = () =>
  useWindowStore((s) => s.keyboardState.openState === "didShow" || s.keyboardState.openState === "willShow");

export const useKeyboardHidden = () => useWindowStore((s) => s.keyboardState.openState === "didHide");

export const useKeyboardHeight = () => useWindowStore((s) => s.keyboardState.info?.keyboardHeight ?? 0);

/// ^^^ LEAF SELECTORS

/// ˅˅˅ LISTERNERS

const setKeyboardState = (keyboardState: TKeyboardInfoState) =>
  useWindowStore.getState().setKeyboardState(keyboardState);
const setWindowSizes = (windowSizes: TWindowSize) => useWindowStore.getState().setWindowSizes(windowSizes);

if (Capacitor.isNativePlatform()) {
  // Unfolding an iPhone Duo posts a keyboard show with no height, though no keyboard is up.
  Keyboard.addListener("keyboardWillShow", (info) => {
    setKeyboardState({ info: info, openState: info.keyboardHeight > 0 ? "willShow" : "didHide" });
  });

  Keyboard.addListener("keyboardDidShow", (info) => {
    setKeyboardState({ info: info, openState: info.keyboardHeight > 0 ? "didShow" : "didHide" });
  });

  Keyboard.addListener("keyboardWillHide", () => {
    // The plugin sends willHide a run-loop turn late and didHide at once, so where UIKit posts
    // the two together (folding or unfolding an iPhone Duo) willHide lands after didHide, and
    // would leave the keyboard looking up until it was next used.
    if (useWindowStore.getState().keyboardState.openState === "didHide") return;
    setKeyboardState({
      info: useWindowStore.getState().keyboardState.info,
      openState: "willHide",
    });
  });

  Keyboard.addListener("keyboardDidHide", () => {
    setKeyboardState({
      info: useWindowStore.getState().keyboardState.info,
      openState: "didHide",
    });
  });
} else {
  setKeyboardState({ info: undefined, openState: "didHide" });
}

const applyInsets = (insets: SafeAreaInsets) =>
  setWindowSizes({
    insets: insets,
    navBarBottom:
      Capacitor.getPlatform() === "ios" ? "guestureIos" : insets.insets.bottom > 30 ? "buttonBlock" : "guestureAndroid",
  });

/**
 * Reads the insets again. The plugin only reports them after a rotation, so a window resized
 * any other way, as the iPad's zoom does, asks for them here.
 */
const refreshSafeAreaInsets = () =>
  void SafeArea.getSafeAreaInsets().then((insets) => {
    if (insets) applyInsets(insets);
  });

if (Capacitor.isNativePlatform()) {
  refreshSafeAreaInsets();
  if (Capacitor.getPlatform() === "ios") void ScreenZoom.addListener("applied", refreshSafeAreaInsets);

  SafeArea.removeAllListeners();
  SafeArea.addListener("safeAreaChanged", (insets) => {
    if (insets) applyInsets(insets);
  });
} else {
  // Not native
  setWindowSizes({
    insets: { insets: { top: 0, left: 0, right: 0, bottom: 0 } },
    navBarBottom: "webView",
  });
}
/// ^^^ LISTERNERS
